import { Checkbox } from '@carbon/react';
import { PrimitiveAtom, atom, useAtomValue, useSetAtom } from 'jotai';
import {
  LayerAtom,
  AllLayersAtom,
  allLayersBaseAtom,
  useCreateAllLayersAtom,
  wellsV2HorizontalLayerAtom,
  wellsV2HorizontalProducingLayerAtom,
  wellsV2HorizontalDucLayerAtom,
  wellsV2HorizontalPermitLayerAtom,
  wellsV2HorizontalShutInLayerAtom,
  wellsV2HorizontalExpiredPermitLayerAtom,
  wellsV2HorizontalInactiveLayerAtom,
  wellsV2HorizontalNonDerivedLayerAtoms,
  wellsV2VerticalLayerAtom,
  wellsV2VerticalProducingLayerAtom,
  wellsV2VerticalDucLayerAtom,
  wellsV2VerticalPermitLayerAtom,
  wellsV2VerticalShutInLayerAtom,
  wellsV2VerticalExpiredPermitLayerAtom,
  wellsV2VerticalInactiveLayerAtom,
  wellsV2VerticalNonDerivedLayerAtoms,
  setWellsV2HorizontalAndChildrenAtom,
  setWellsV2VerticalAndChildrenAtom,
  setWellsV2AndAllChildrenAtom,
  syncWellsV2WithChildrenAtom,
  NonDerivedLayerAtoms,
} from './atoms';

import style from 'components/map/map.module.scss';
import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import {
  SLOT_CATEGORY_LABELS,
  WELL_STATUS_SLOT_CATEGORY_KEYS,
} from 'utils/slot-categories';
import { CollapsibleSection } from 'components/map/collapsible-section';

// Represents a checkbox to select/unselect a map layer.
//
// If atoms is provided, it means that the checkbox is
// used to toggle multiple checkboxes at the same time.
// The elements of the array represents the atoms linked to
// the other checkboxes
const Layer = ({
  atom,
  id,
  label,
  atoms,
  indeterminate,
}: {
  atom: LayerAtom | AllLayersAtom;
  id: string;
  label: string;
  atoms?: PrimitiveAtom<boolean>[];
  indeterminate?: boolean;
}) => {
  const value = useAtomValue(atom);
  const setValue = useSetAtom(atom);

  return (
    <Checkbox
      id={id}
      labelText={label}
      checked={value}
      indeterminate={indeterminate}
      onChange={(_evt, { checked }: { checked: boolean }) => {
        setValue(checked, atoms);
      }}
    />
  );
};

// Status filter checkbox component for Wells V2
const StatusFilter = ({
  id,
  label,
  atom,
  className,
  syncCallback,
}: {
  id: string;
  label: string;
  atom: PrimitiveAtom<boolean>;
  className?: string;
  syncCallback?: (checked: boolean) => void;
}) => {
  const checked = useAtomValue(atom);
  const setChecked = useSetAtom(atom);

  return (
    <div className={classNames(style.coloredFilter, className)}>
      <Checkbox
        id={id}
        labelText={label}
        checked={checked}
        onChange={(_evt, { checked }: { checked: boolean }) => {
          setChecked(checked);
          // If a sync callback is provided, call it to sync parent state
          if (syncCallback) {
            syncCallback(checked);
          }
        }}
      />
    </div>
  );
};

// The swatch class per Create New Well status, keyed like the main map's (map.module.scss).
const NON_DERIVED_SWATCH: Readonly<Record<string, string | undefined>> = {
  proposal: style.proposal,
  planned: style.planned,
  pre_permit: style.prePermit,
  apd: style.apd,
};

/**
 * One toggle per Create New Well status, in display order, each drawing only its own status
 * (INS-13300).
 *
 * @param {object} props - Component props.
 * @param {string} props.direction - `horizontal` or `vertical`, for the ids.
 * @param {NonDerivedLayerAtoms} props.atoms - The direction's toggle atom per status.
 * @param {Function} props.syncCallback - Re-syncs the parent checkboxes after a toggle.
 * @return {JSX.Element} The four toggles.
 */
function NonDerivedStatusFilters({
  direction,
  atoms,
  syncCallback,
}: {
  direction: 'horizontal' | 'vertical';
  atoms: NonDerivedLayerAtoms;
  syncCallback: (checked: boolean) => void;
}) {
  return (
    <>
      {WELL_STATUS_SLOT_CATEGORY_KEYS.map((status) => (
        <StatusFilter
          key={status}
          id={`package-wells-v2-${direction}-${status.replace('_', '-')}-layer`}
          label={SLOT_CATEGORY_LABELS[status]}
          atom={atoms[status]!}
          className={NON_DERIVED_SWATCH[status]}
          syncCallback={syncCallback}
        />
      ))}
    </>
  );
}

const ScopedPackageLayerSelection = ({
  layerAtoms,
  hideWellsLayer = false,
}: {
  layerAtoms: {
    assets: PrimitiveAtom<boolean>;
    devAreas: PrimitiveAtom<boolean>;
    title: PrimitiveAtom<boolean>;
    wellsV2: PrimitiveAtom<'CHECKED' | 'UNCHECKED' | 'INDETERMINATE'>;
  };
  hideWellsLayer?: boolean;
}) => {
  const assetAtom = layerAtoms.assets;
  const developmentAreaAtom = layerAtoms.devAreas;
  const titleAtom = layerAtoms.title;
  const wellsV2Atom = layerAtoms.wellsV2;
  const assetsLayer = useAtomValue(assetAtom);
  const developmentAreaLayer = useAtomValue(developmentAreaAtom);
  const titleLayer = useAtomValue(titleAtom);
  const wellsV2Layer = useAtomValue(wellsV2Atom);

  // Wells V2 Horizontal category
  const wellsV2HorizontalLayer = useAtomValue(wellsV2HorizontalLayerAtom);

  // Wells V2 Vertical category
  const wellsV2VerticalLayer = useAtomValue(wellsV2VerticalLayerAtom);

  // Use the setter atoms
  const setWellsV2HorizontalAndChildren = useSetAtom(
    setWellsV2HorizontalAndChildrenAtom
  );
  const setWellsV2VerticalAndChildren = useSetAtom(
    setWellsV2VerticalAndChildrenAtom
  );
  const setWellsV2AndAllChildren = useSetAtom(setWellsV2AndAllChildrenAtom);

  // Include the other setters for individual status atoms
  const setHorizontalProducing = useSetAtom(
    wellsV2HorizontalProducingLayerAtom
  );
  const setHorizontalDuc = useSetAtom(wellsV2HorizontalDucLayerAtom);
  const setHorizontalPermit = useSetAtom(wellsV2HorizontalPermitLayerAtom);
  const setHorizontalShutIn = useSetAtom(wellsV2HorizontalShutInLayerAtom);
  const setHorizontalExpiredPermit = useSetAtom(
    wellsV2HorizontalExpiredPermitLayerAtom
  );
  const setHorizontalInactive = useSetAtom(wellsV2HorizontalInactiveLayerAtom);

  // Get setters for vertical status filters
  const setVerticalProducing = useSetAtom(wellsV2VerticalProducingLayerAtom);
  const setVerticalDuc = useSetAtom(wellsV2VerticalDucLayerAtom);
  const setVerticalPermit = useSetAtom(wellsV2VerticalPermitLayerAtom);
  const setVerticalShutIn = useSetAtom(wellsV2VerticalShutInLayerAtom);
  const setVerticalExpiredPermit = useSetAtom(
    wellsV2VerticalExpiredPermitLayerAtom
  );
  const setVerticalInactive = useSetAtom(wellsV2VerticalInactiveLayerAtom);

  // Get the actual atom to use when custom is provided
  const setCustomWellsV2 = useSetAtom(layerAtoms.wellsV2);

  const localAllLayersAtom = useMemo(() => atom(true), []);
  const allLayersAtom = useCreateAllLayersAtom(
    layerAtoms ? localAllLayersAtom : allLayersBaseAtom
  );
  const setAllLayers = useSetAtom(allLayersAtom);
  const atoms = [assetAtom, developmentAreaAtom, titleAtom];

  // Setup for isIndeterminate state
  const [isIndeterminate, setIndeterminate] = useState(false);

  // Standard effect for the All Layers checkbox indeterminate state
  useEffect(() => {
    const allLayers = [assetsLayer, developmentAreaLayer, titleLayer];
    if (allLayers.every((el) => el)) {
      setAllLayers(true, undefined);
      setIndeterminate(false);
      return;
    }
    if (allLayers.some((el) => el)) {
      setAllLayers(false, undefined);
      setIndeterminate(true);
      return;
    }
    setAllLayers(false, undefined);
    setIndeterminate(false);
  }, [assetsLayer, developmentAreaLayer, setAllLayers, titleLayer]);

  const setSyncWellsV2WithChildren = useSetAtom(syncWellsV2WithChildrenAtom);

  const handleStatusChange = (
    checked: boolean,
    setterFn: (checked: boolean) => void
  ) => {
    setterFn(checked);
    setSyncWellsV2WithChildren();
  };

  // Horizontal status change handlers
  const handleHorizontalProducingChange = (checked: boolean) =>
    handleStatusChange(checked, setHorizontalProducing);
  const handleHorizontalDucChange = (checked: boolean) =>
    handleStatusChange(checked, setHorizontalDuc);
  const handleHorizontalPermitChange = (checked: boolean) =>
    handleStatusChange(checked, setHorizontalPermit);
  const handleHorizontalShutInChange = (checked: boolean) =>
    handleStatusChange(checked, setHorizontalShutIn);
  const handleHorizontalExpiredPermitChange = (checked: boolean) =>
    handleStatusChange(checked, setHorizontalExpiredPermit);
  const handleHorizontalInactiveChange = (checked: boolean) =>
    handleStatusChange(checked, setHorizontalInactive);
  /**
   * A Create New Well status toggle: its StatusFilter has already set its own atom, so only
   * the parent checkboxes need re-syncing.
   * @return {void}
   */
  const handleNonDerivedChange = () => setSyncWellsV2WithChildren();

  // Vertical status change handlers
  const handleVerticalProducingChange = (checked: boolean) =>
    handleStatusChange(checked, setVerticalProducing);
  const handleVerticalDucChange = (checked: boolean) =>
    handleStatusChange(checked, setVerticalDuc);
  const handleVerticalPermitChange = (checked: boolean) =>
    handleStatusChange(checked, setVerticalPermit);
  const handleVerticalShutInChange = (checked: boolean) =>
    handleStatusChange(checked, setVerticalShutIn);
  const handleVerticalExpiredPermitChange = (checked: boolean) =>
    handleStatusChange(checked, setVerticalExpiredPermit);
  const handleVerticalInactiveChange = (checked: boolean) =>
    handleStatusChange(checked, setVerticalInactive);

  // Handle wells V2 main checkbox change
  const handleWellsV2Change = (
    _evt: React.ChangeEvent<HTMLInputElement>,
    { checked }: { checked: boolean }
  ) => {
    // Update our local atoms
    setWellsV2AndAllChildren(checked);

    // Also update the custom atom if provided
    if (setCustomWellsV2) {
      setCustomWellsV2(checked ? 'CHECKED' : 'UNCHECKED');
    }
  };

  // Handle horizontal category checkbox change
  const handleHorizontalChange = (
    _evt: React.ChangeEvent<HTMLInputElement>,
    { checked }: { checked: boolean }
  ) => {
    // Update our local atoms
    setWellsV2HorizontalAndChildren(checked);

    // Force a sync of the parent-child relationship
    setSyncWellsV2WithChildren();
  };

  // Handle vertical category checkbox change
  const handleVerticalChange = (
    _evt: React.ChangeEvent<HTMLInputElement>,
    { checked }: { checked: boolean }
  ) => {
    // Update our local atoms
    setWellsV2VerticalAndChildren(checked);

    // Force a sync of the parent-child relationship
    setSyncWellsV2WithChildren();
  };

  return (
    <>
      <div className={style.titleArea}>
        <div className={style.allCheckBox}>
          <Layer
            atom={allLayersAtom}
            id={'scoped-layers'}
            label="Layers"
            atoms={atoms}
            indeterminate={isIndeterminate}
          />
        </div>
      </div>
      <div className={style.groupChecks}>
        <div className={style.scopedAssets}>
          <Layer atom={assetAtom} id={'package-assets-layer'} label="Assets" />
        </div>
        <div className={style.scopedUnits}>
          <Layer
            atom={developmentAreaAtom}
            id={'package-development-areas-layer'}
            label="Units"
          />
        </div>
        <div className={style.scopedTitle}>
          <Layer atom={titleAtom} id={'package-title-layer'} label="Title" />
        </div>
      </div>

      {!hideWellsLayer && (
        <div>
          {/* Wells Beta Section - Using the imported CollapsibleSection component */}
          <div className={classNames(style.layersSelector)}>
            <CollapsibleSection
              id="package-wells-v2-layer"
              title="Wells"
              checked={wellsV2Layer === 'CHECKED'}
              indeterminate={wellsV2Layer === 'INDETERMINATE'}
              onChange={handleWellsV2Change}
              defaultOpen={true}
            >
              {/* Horizontal Wells Category */}
              <div>
                <div
                  className={classNames(style.titleArea, style.categoryHeader)}
                >
                  <div className={classNames(style.allCheckBox)}>
                    <Checkbox
                      id="package-wells-v2-horizontal-layer"
                      labelText="Horizontal"
                      checked={wellsV2HorizontalLayer === 'CHECKED'}
                      indeterminate={wellsV2HorizontalLayer === 'INDETERMINATE'}
                      onChange={handleHorizontalChange}
                    />
                  </div>
                </div>
                <div className={style.groupChecks}>
                  <div className={style.statusFilters}>
                    <StatusFilter
                      id="package-wells-v2-horizontal-producing-layer"
                      label="Producing"
                      atom={wellsV2HorizontalProducingLayerAtom}
                      className={style.producing}
                      syncCallback={handleHorizontalProducingChange}
                    />
                    <StatusFilter
                      id="package-wells-v2-horizontal-duc-layer"
                      label="DUC"
                      atom={wellsV2HorizontalDucLayerAtom}
                      className={style.duc}
                      syncCallback={handleHorizontalDucChange}
                    />
                    <StatusFilter
                      id="package-wells-v2-horizontal-permit-layer"
                      label="Permit"
                      atom={wellsV2HorizontalPermitLayerAtom}
                      className={style.permit}
                      syncCallback={handleHorizontalPermitChange}
                    />
                    <StatusFilter
                      id="package-wells-v2-horizontal-shut-in-layer"
                      label="Shut In"
                      atom={wellsV2HorizontalShutInLayerAtom}
                      className={style.shutIn}
                      syncCallback={handleHorizontalShutInChange}
                    />
                    <StatusFilter
                      id="package-wells-v2-horizontal-expired-permit-layer"
                      label="Expired Permit"
                      atom={wellsV2HorizontalExpiredPermitLayerAtom}
                      className={style.expiredPermit}
                      syncCallback={handleHorizontalExpiredPermitChange}
                    />
                    <StatusFilter
                      id="package-wells-v2-horizontal-inactive-layer"
                      label="Inactive"
                      atom={wellsV2HorizontalInactiveLayerAtom}
                      className={style.inactive}
                      syncCallback={handleHorizontalInactiveChange}
                    />
                    <NonDerivedStatusFilters
                      direction="horizontal"
                      atoms={wellsV2HorizontalNonDerivedLayerAtoms}
                      syncCallback={handleNonDerivedChange}
                    />
                  </div>
                </div>
              </div>

              {/* Vertical Wells Category */}
              <div>
                <div
                  className={classNames(style.titleArea, style.categoryHeader)}
                >
                  <div className={classNames(style.allCheckBox)}>
                    <Checkbox
                      id="package-wells-v2-vertical-layer"
                      labelText="Vertical"
                      checked={wellsV2VerticalLayer === 'CHECKED'}
                      indeterminate={wellsV2VerticalLayer === 'INDETERMINATE'}
                      onChange={handleVerticalChange}
                    />
                  </div>
                </div>
                <div className={style.groupChecks}>
                  <div className={style.statusFilters}>
                    <StatusFilter
                      id="package-wells-v2-vertical-producing-layer"
                      label="Producing"
                      atom={wellsV2VerticalProducingLayerAtom}
                      className={style.producing}
                      syncCallback={handleVerticalProducingChange}
                    />
                    <StatusFilter
                      id="package-wells-v2-vertical-duc-layer"
                      label="DUC"
                      atom={wellsV2VerticalDucLayerAtom}
                      className={style.duc}
                      syncCallback={handleVerticalDucChange}
                    />
                    <StatusFilter
                      id="package-wells-v2-vertical-permit-layer"
                      label="Permit"
                      atom={wellsV2VerticalPermitLayerAtom}
                      className={style.permit}
                      syncCallback={handleVerticalPermitChange}
                    />
                    <StatusFilter
                      id="package-wells-v2-vertical-shut-in-layer"
                      label="Shut In"
                      atom={wellsV2VerticalShutInLayerAtom}
                      className={style.shutIn}
                      syncCallback={handleVerticalShutInChange}
                    />
                    <StatusFilter
                      id="package-wells-v2-vertical-expired-permit-layer"
                      label="Expired Permit"
                      atom={wellsV2VerticalExpiredPermitLayerAtom}
                      className={style.expiredPermit}
                      syncCallback={handleVerticalExpiredPermitChange}
                    />
                    <StatusFilter
                      id="package-wells-v2-vertical-inactive-layer"
                      label="Inactive"
                      atom={wellsV2VerticalInactiveLayerAtom}
                      className={style.inactive}
                      syncCallback={handleVerticalInactiveChange}
                    />
                    <NonDerivedStatusFilters
                      direction="vertical"
                      atoms={wellsV2VerticalNonDerivedLayerAtoms}
                      syncCallback={handleNonDerivedChange}
                    />
                  </div>
                </div>
              </div>
            </CollapsibleSection>
          </div>
        </div>
      )}
    </>
  );
};

export { ScopedPackageLayerSelection };
